import createNestedObject from '../createNestedObject';
import isObject from '../isObject';

export const project = 'tealium';

export const getSiteEvents = ({
	payload: {
		// eslint-disable-next-line camelcase
		site_events = {}
	} = {}
}) => {
	return isObject(site_events) && Object.keys(site_events).join(',');
};

// Sending function
export const sending = ({ data, event, preview, win }) => {
	// Three expected outcomes
	// '_dl' writes to window._dl object. Exclusive to ddc.page.view events
	// '_trackAnalytics' will track the event and send to tealium. All other events.
	// '_dl _trackAnalytics' track + send to tealium, but we also want the _dl payload to reflect it
	let sender;

	if (win.DDC.dataLayer.page.pageInfo.isVdp && event === 'ddc.page.view') {
		// This is where we update inventoryHistory once pageview event finishes
		const tempInventoryHistory = window.sessionStorage.getItem(
			'inventoryHistoryTemp'
		);
		window.sessionStorage.setItem('inventoryHistory', tempInventoryHistory);
		window.sessionStorage.removeItem('inventoryHistoryTemp');
	}

	if (
		['ddc.inventory.filterSearch.click', 'widgetUserEvent'].includes(event)
	) {
		if (!preview) {
			win._trackAnalytics(data);
			createNestedObject(win, '_dl', data);
		}
		sender = '_trackAnalytics() _dl';
	} else if (event === 'ddc.page.view') {
		if (!preview) {
			createNestedObject(win, '_dl', data);
		}
		sender = '_dl';
	} else {
		if (!preview) {
			win._trackAnalytics(data);
		}
		sender = '_trackAnalytics()';
	}

	const siteEvents = getSiteEvents({
		payload: data
	});

	let clientEventName = siteEvents || event;

	if (data?.event || data?.result) {
		clientEventName = `${data?.event || ''}-${siteEvents || ''}-${
			data?.result || ''
		}`;
	}

	return {
		reportsForEventViewer: [
			{
				clientEventName,
				payload: data,
				sender
			}
		],
		reportsForNewRelic: [
			{
				clientEventName,
				sender
			}
		]
	};
};
// Showroom helper functions

export const getShowroomPageName = ({ window }) => {
	const {
		pageInfo: { isShowroomOverview },
		trackingTags: { make, model }
	} = window.DDC.dataLayer.page;
	return isShowroomOverview
		? `vehicle landing:${make} ${model}`
		: 'all vehicles';
};

export const getShowroomOverviewSiteEvent = ({ window }) => {
	const {
		pageInfo: { isShowroomOverview }
	} = window.DDC.dataLayer.page;

	return isShowroomOverview ? { showroom_view: true } : {};
};

// Vehicle payload functions
export const getVehicleResultList = ({ vehicles }) => {
	const formattedVehicles = vehicles.map(
		({ make, model, modelYear }) => `${modelYear} ${make} ${model}`
	);

	return formattedVehicles;
};

export const getVehicleCompareList = ({ vehicles }) => {
	const formattedVehicles = vehicles.map(
		({ make, model, modelYear, trim }) =>
			`${modelYear} ${make} ${model} ${trim}`
	);

	return formattedVehicles;
};

export const getVehicleDLPayload = ({ vehicles }) => {
	const formattedVehicles = vehicles.map(
		({
			exteriorColor,
			interiorColor,
			cityFuelEfficiency,
			highwayFuelEfficiency,
			engine,
			make,
			model,
			modelYear,
			odometer,
			optionCodes,
			packages,
			internetPrice,
			transmission,
			trim,
			stockNumber,
			vin
		}) => ({
			color_exterior: exteriorColor,
			color_interior: interiorColor,
			engine,
			epa_city: cityFuelEfficiency,
			epa_highway: highwayFuelEfficiency,
			make,
			model_year: modelYear,
			nameplate: model,
			odometer,
			options: optionCodes,
			packages,
			price: internetPrice,
			transmission,
			trim,
			vehicle_stock_number: stockNumber,
			vin
		})
	);

	return formattedVehicles;
};

export const getInventoryTypeForCompareVehicles = ({ vehicles }) => {
	// This is needed due to the COMPARE pages inventoryTypes being dependent on the vehicles array
	// For example, if I used /new-inventory/ and have both used vehicles as the id on the parems then the inventoryType will be used
	let hasNew = false;
	let hasPreOwned = false;
	let hasBoth = false;

	vehicles.forEach(({ inventoryType }) => {
		if (inventoryType === 'new') {
			hasNew = true;
		}

		if (inventoryType === 'used') {
			hasPreOwned = true;
		}

		if (hasNew && hasPreOwned) {
			hasBoth = true;
		}
	});

	if (hasBoth) {
		return 'new and pre owned';
	}

	return hasNew ? 'new' : 'pre owned';
};

export const getSiteEventsForCompareVehicles = ({ vehicles }) => {
	const inventoryType = getInventoryTypeForCompareVehicles({ vehicles });
	const siteEventCondition =
		inventoryType === 'new and pre owned'
			? 'both'
			: inventoryType.replace(' ', '_');

	return {
		compare_vehicle: true,
		[`search_inventory_${siteEventCondition}`]: true
	};
};

// VLP/VDP functions

export const checkForUniqueInventoryView = ({ window }) => {
	const inventoryHistory =
		JSON.parse(window.sessionStorage.getItem('inventoryHistory') || '[]') ||
		[];

	const queryFacets = (
		JSON.stringify(window.DDC.dataLayer.page.queryFacets) || ''
	).replace(/[^\w\s!?]/g, '');

	const currentSearch =
		window.DDC.dataLayer.page.pageInfo.pagePath + queryFacets;

	let isNewSearch = true;

	inventoryHistory?.forEach((entry) => {
		if (currentSearch === entry) {
			isNewSearch = false;
		}
	});

	if (isNewSearch) {
		// We want to temporarily store this in another key because getVDPSiteEvents gets called twice
		// We will deal with the actual inventoryHistory update in sending function
		inventoryHistory.push(currentSearch);
		window.sessionStorage.setItem(
			'inventoryHistoryTemp',
			JSON.stringify(inventoryHistory)
		);
	}

	return isNewSearch;
};

export const getVDPSiteEvents = ({ window, vehicles: [vehicle] }) => {
	const siteEvents = {};
	const setSiteEvents = checkForUniqueInventoryView({ window });
	const bypassListings = !document.referrer.includes('-inventory');

	if (setSiteEvents) {
		siteEvents.search_inventory_details = true;

		if (vehicle.newOrUsed === 'new' && bypassListings) {
			siteEvents.search_inventory_new = true;
		} else if (vehicle.newOrUsed === 'used' && bypassListings) {
			siteEvents.search_inventory_pre_owned = true;
		}

		if (bypassListings) {
			siteEvents.vlp_view = false;
		} else {
			siteEvents.vlp_view = true;
		}
	}

	return siteEvents;
};

// Miscellaneous helpers

export const getCurrentTime = () => {
	return new Date().toISOString().slice(0, 19).replace(/[-T:]/g, '|');
};

export const getOtherPageName = ({ window }) => {
	const { pageName } = window.DDC.dataLayer.page.pageInfo;

	return pageName.replace(/_/g, ' ')?.toLowerCase();
};

export const getInventoryFilters = ({ window }) => {
	const filters = window.DDC.dataLayer.page.queryFacets;
	const entries = Object.entries(filters);

	return entries
		.map(([key, value]) => {
			return value
				.split(',')
				.map((val) => `${key}:${val}`)
				.join('|');
		})
		.join('|');
};

export const hasAutohookOffer = () => {
	return document.querySelector('.autohookOffer') ? true : null;
};

export const getZipCodeViaDOM = () => {
	return document.querySelector('input[name="zipc1"]')?.value;
};

export const formatClickToCallDepartment = ({ eventData }) => {
	return eventData.clickToCallDepartment.toLowerCase().replaceAll(' ', '_');
};

// FORM FUNCTIONS //

export const getFormContactMedium = ({ eventData, window: { DDC } }) => {
	// The window object is used for form.confirmation properties
	// The event object is used for form.submission properties
	// REASON: form confirmation is used on pageView, NOT form.confirmation event, submission uses the event
	const hasEmail = DDC.dataLayer.confirmation?.email || eventData.emailExists;
	const hasPhone = DDC.dataLayer.confirmation?.phone || eventData.phoneExists;
	const hasBoth = hasEmail && hasPhone;

	if (hasBoth) {
		return 'both';
	} else if (hasPhone) {
		return 'phone';
	} else if (hasEmail) {
		return 'email';
	}

	return undefined;
};

export const getMakeFromInput = () => {
	return document.querySelector('input[name="make"]')?.value;
};

export const getYearFromInput = () => {
	return document.querySelector('input[name="year"]')?.value;
};

export const getVehicleToService = ({ eventData }) => {
	return [
		{
			year: getYearFromInput(),
			make: getMakeFromInput(),
			model: eventData.formVehicleModel
		}
	];
};
